import React, { useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useFeatureFlagsContext } from "@/contexts/FeatureFlagsContext";
import { useTheme } from "@/contexts/ThemeContext";
import { apiClient, Report, ACCESS_LEVELS } from "@rti/shared";
import type { NavItemPublic } from "@rti/shared";
import {
  LayoutDashboard,
  TrendingUp,
  Calendar,
  DollarSign,
  BarChart3,
  Newspaper,
  Scale,
  Home,
  PanelLeft,
  ChevronRight,
  FileSpreadsheet,
  ChartNoAxesCombined,
  FileChartColumnIncreasingIcon,
  BrainCircuit,
  Package,
  Globe,
  Settings,
  Bell,
  Users,
  BookOpen,
  LineChart,
  PieChart,
  Building2,
  Factory,
  Zap,
  Star,
  ShieldCheck,
  Lock,
  X,
  Layers,
  Database,
  Activity,
  HandCoins,
  LayoutGrid,
  Ship,
} from "lucide-react";
import type { PolymerTab } from "@/types/polymer-dashboard.types";

interface LeftSideBarProps {
  polymerTabs?: PolymerTab[];
  activeTab?: string;
  onTabChange?: (tab: string) => void;
  polymerName?: string;
}

interface SidebarSubmenuItem {
  label: string;
  path: string;
  polymer?: ResinCode;
  gradient?: string;
  textColor?: string;
  isAvailable: boolean;
  isPremiumRequired?: boolean;
  icon?: React.ElementType;
}

type ResinCode = "PE" | "PP" | "PS" | "PVC" | "PET" | "PC";

const gradients = ["green", "red", "purple", "plum", "green", "red"];

const ICON_MAP: Record<string, React.ElementType> = {
  Home, LayoutDashboard, FileSpreadsheet, BarChart3, Calendar, DollarSign,
  ChartNoAxesCombined, Scale, Newspaper, TrendingUp, Package, Globe,
  Settings, Bell, Users, BookOpen, LineChart, PieChart, Building2,
  Factory, Zap, Star, ShieldCheck, Lock, Layers, Database, Activity,
  FileChartColumnIncreasing: FileChartColumnIncreasingIcon, BrainCircuit, Ship,
};
const textColors = ["green", "red", "light-purple", "salmon", "green", "red"];

const LeftSideBar: React.FC<LeftSideBarProps> = ({
  polymerTabs,
  activeTab,
  onTabChange,
  polymerName,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { isDark } = useTheme();

  const chromeStyle: React.CSSProperties | undefined = isDark
    ? undefined
    : {
        background: 'rgba(231,242,224,0.85)',
        borderColor: 'transparent',
        position: 'relative',
        zIndex: 30,
        boxShadow: '6px 0 16px -4px rgba(16,24,16,0.22)',
      };
  const { getFlag, isLoading: isFeatureFlagsLoading } = useFeatureFlagsContext();
  const hasPremiumAccess = !!user && (user.effective_membership_id ?? 0) >= ACCESS_LEVELS.COMPOUND;
  const [isExpanded, setIsExpanded] = useState(false);
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const [flyoutTop, setFlyoutTop] = useState(0);
  const [lockedPopoverId, setLockedPopoverId] = useState<string | null>(null);
  const [activeReports, setActiveReports] = useState<Report[]>([]);
  const [allNavItems, setAllNavItems] = useState<NavItemPublic[]>([]);
  const sidebarRef = React.useRef<HTMLElement>(null);
  const closeTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  React.useEffect(() => {
    if (!user?.user_id) {
      setActiveReports([]);
      return;
    }

    let mounted = true;

    apiClient
      .getUserAccessibleReports(user.user_id)
      .then((reports) => {
        if (!mounted) return;
        setActiveReports((reports || []).filter((report) => report.is_active));
      })
      .catch((error) => {
        if (!mounted) return;
        const status = (error as any).response?.status;
        if (status === 403 || status === 404) {
          navigate("/authorization");
          return;
        }
        setActiveReports([]);
      });

    return () => {
      mounted = false;
    };
  }, [user?.user_id]);

  React.useEffect(() => {
    if (!user?.user_id) return;
    let mounted = true;
    apiClient
      .getNavItems()
      .then((items) => {
        if (!mounted) return;
        setAllNavItems(items);
      })
      .catch(() => {
        if (!mounted) return;
        setAllNavItems([]);
      });
    return () => { mounted = false; };
  }, [user?.user_id]);

  const cancelClose = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  const scheduleClose = () => {
    cancelClose();
    closeTimeoutRef.current = setTimeout(() => {
      setHoveredItem(null);
    }, 300);
  };

  React.useEffect(() => {
    return () => { cancelClose(); };
  }, []);

  const handleMenuHover = (key: string, e: React.MouseEvent<HTMLElement>) => {
    cancelClose();
    const rect = e.currentTarget.getBoundingClientRect();
    setFlyoutTop(rect.top);
    setHoveredItem(key);
  };

  const handleFlyoutEnter = (key: string) => {
    cancelClose();
    setHoveredItem(key);
  };

  const getFlyoutLeft = () => {
    if (!sidebarRef.current) return 0;
    return sidebarRef.current.getBoundingClientRect().right;
  };

  const useResinDashboards = !isFeatureFlagsLoading && getFlag("marketintel_resin_dashboards");

  const getPolymerDashboardPath = (polymer: string, reportId: number): string => {
    if (!useResinDashboards) return `/marketIntel/${reportId}`;
    return `/marketIntel/${polymer.toLowerCase()}`;
  };

  const resins = useMemo(() => {
    const resinMap = new Map<string, {
      polymer: string;
      fullName: string;
      reportId: number;
      sortOrder: number;
      hasDashboard: boolean;
      hasAcc: boolean;
      hasForecast: boolean;
    }>();

    [...activeReports]
      .sort((a, b) => {
        const aSort = a.sort_order ?? 999;
        const bSort = b.sort_order ?? 999;
        if (aSort !== bSort) return aSort - bSort;
        return a.name.localeCompare(b.name);
      })
      .forEach((report) => {
        const polymer = (report.abbreviation || "").trim().toUpperCase();
        if (!polymer) return;

        const fullName = (report.name || polymer).trim().replace(/\s+dashboard$/i, "") || polymer;
        const existing = resinMap.get(polymer);

        if (!existing) {
          resinMap.set(polymer, {
            polymer,
            fullName,
            reportId: report.report_id,
            sortOrder: report.sort_order ?? 999,
            hasDashboard: !!report.has_dashboard,
            hasAcc: !!report.has_acc,
            hasForecast: !!report.has_forecast,
          });
          return;
        }

        existing.hasDashboard = existing.hasDashboard || !!report.has_dashboard;
        existing.hasAcc = existing.hasAcc || !!report.has_acc;
        existing.hasForecast = existing.hasForecast || !!report.has_forecast;
      });

    return Array.from(resinMap.values()).sort((a, b) => {
      if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
      return a.fullName.localeCompare(b.fullName);
    });
  }, [activeReports]);

  const isActive = (path: string) => location.pathname === path;

  const userMembershipId = user?.effective_membership_id ?? 0;

  const menuItems = useMemo(() => {
    return allNavItems
      .filter(navItem => !navItem.min_membership_id || userMembershipId >= navItem.min_membership_id)
      .map((navItem) => {
      const icon = ICON_MAP[navItem.icon_name] ?? Home;
      let submenuItems: SidebarSubmenuItem[] | undefined;
      let isVisible = true;

      if (navItem.submenu_type === 'polymer_dashboard') {
        isVisible = resins.some(r => r.hasDashboard);
        submenuItems = resins.map((resin, index) => ({
          label: `${resin.fullName} (${resin.polymer})`,
          path: useResinDashboards ? `/marketIntel/${resin.polymer.toLowerCase()}` : `/marketIntel/${resin.reportId}`,
          polymer: resin.polymer as ResinCode,
          gradient: gradients[index % gradients.length],
          textColor: textColors[index % textColors.length],
          isAvailable: resin.hasDashboard,
        }));
      } else if (navItem.submenu_type === 'polymer_snapshot') {
        isVisible = resins.some(r => r.hasAcc);
        submenuItems = resins.map((resin, index) => ({
          label: `${resin.fullName} (${resin.polymer})`,
          path: `/snapshot/${resin.polymer.toLowerCase()}`,
          polymer: resin.polymer as ResinCode,
          gradient: gradients[index % gradients.length],
          textColor: textColors[index % textColors.length],
          isAvailable: resin.hasAcc,
        }));
      } else if (navItem.submenu_type === 'polymer_forecast') {
        isVisible = resins.some(r => r.hasForecast);
        submenuItems = resins.map((resin, index) => ({
          label: `${resin.fullName} (${resin.polymer})`,
          path: `/forecast/${resin.polymer.toLowerCase()}`,
          polymer: resin.polymer as ResinCode,
          gradient: gradients[index % gradients.length],
          textColor: textColors[index % textColors.length],
          isAvailable: resin.hasForecast,
          isPremiumRequired: true,
        }));
      } else if (navItem.submenu_type === 'static' && navItem.submenu_items) {
        try {
          const parsed = JSON.parse(navItem.submenu_items) as Array<{ label: string; path: string; icon_name?: string }>;
          submenuItems = parsed.map(sub => ({
            label: sub.label,
            path: sub.path,
            icon: sub.icon_name ? ICON_MAP[sub.icon_name] : undefined,
            isAvailable: true,
          }));
        } catch {
          submenuItems = undefined;
        }
      }

      return {
        key: navItem.nav_key,
        label: navItem.label,
        path: navItem.path,
        icon,
        hasSubmenu: navItem.has_submenu,
        isVisible,
        submenuItems,
      };
    });
  }, [allNavItems, resins, useResinDashboards, userMembershipId]);

  const visibleItems = menuItems.filter(item => item.isVisible);

  const renderSubItem = (sub: SidebarSubmenuItem, key: string, popoverKey: string) => {
    const isLocked = !!sub.isPremiumRequired && !hasPremiumAccess;
    return (
      <li key={key} className="relative">
        <div
          onClick={() => {
            if (isLocked) { setLockedPopoverId(lockedPopoverId === popoverKey ? null : popoverKey); return; }
            navigate(sub.path);
            setHoveredItem(null);
          }}
          className={`flex items-center gap-3 px-4 py-2 text-sm transition-colors ${
            isLocked
              ? "opacity-50 cursor-not-allowed text-rs-off-white/70"
              : "cursor-pointer text-rs-off-white/70 hover:bg-rs-jet-light hover:text-rs-off-white"
          }`}
        >
          {sub.polymer ? (
            <div className={`flex items-center justify-center w-6 h-6 rounded font-bold text-xs flex-shrink-0 dashboard-${sub.gradient}-gradient text-rs-${sub.textColor}`}>
              {sub.polymer}
            </div>
          ) : sub.icon ? (
            <sub.icon size={20} className="flex-shrink-0 opacity-60" />
          ) : (
            <div className="w-1.5 h-1.5 rounded-full bg-rs-off-white/40 flex-shrink-0" />
          )}
          <span className="flex-1">{sub.label}</span>
          {isLocked && <Lock size={13} className="text-yellow-500 flex-shrink-0" />}
        </div>
        {isLocked && lockedPopoverId === popoverKey && (
          <div className="mx-2 mb-1 rounded-md border border-rs-jet-light bg-rs-jet p-2 text-xs text-rs-off-white shadow-lg">
            <div className="flex items-start gap-1.5">
              <Lock size={12} className="mt-0.5 flex-shrink-0 text-yellow-500" />
              <p className="flex-1 leading-snug"><strong>Compound Plan</strong> or higher required for Pricing &amp; Forecast.</p>
              <button onClick={(e) => { e.stopPropagation(); setLockedPopoverId(null); }} className="flex-shrink-0 opacity-60 hover:opacity-100">
                <X size={12} />
              </button>
            </div>
          </div>
        )}
      </li>
    );
  };

  const resinMenuItems = resins
    .map((resin, index) => ({
    label: resin.polymer,
    fullName: resin.fullName,
    reportId: resin.reportId,
    gradient: gradients[index % gradients.length],
    textColor: textColors[index % textColors.length],
    submenuItems: [
      { label: "Market Intel", path: getPolymerDashboardPath(resin.polymer, resin.reportId), icon: BarChart3, isAvailable: resin.hasDashboard },
      { label: "Monthly Snapshot", path: `/snapshot/${resin.polymer.toLowerCase()}`, icon: Calendar, isAvailable: resin.hasAcc },
      { label: "Pricing & Forecast", path: `/forecast/${resin.polymer.toLowerCase()}`, icon: DollarSign, isAvailable: resin.hasForecast, isPremiumRequired: true },
    ],
  }))
    .filter((resin) => resin.submenuItems.some((sub) => sub.isAvailable));

  return (
    <aside
      ref={sidebarRef}
      data-tour="left-nav"
      style={chromeStyle}
      className={`${isExpanded ? 'w-64' : 'w-16'} h-full min-h-0 bg-rs-off-black border-r border-rs-jet-light flex flex-col transition-all duration-300 flex-shrink-0`}
    >
      {/* Toggle button */}
      <div className={`flex items-center h-12 border-b border-rs-jet-light flex-shrink-0 ${isExpanded ? 'px-4 justify-end' : 'justify-center'}`}>
        <button
          data-tour="left-nav-toggle"
          onClick={() => { setIsExpanded(prev => !prev); setHoveredItem(null); }}
          className="text-rs-off-white/50 hover:text-rs-off-white transition-colors p-1"
          title={isExpanded ? "Collapse sidebar" : "Expand sidebar"}
        >
          <PanelLeft size={18} />
        </button>
      </div>

      {/* Menu Items */}
      <nav className="flex-1 min-h-0 py-4 overflow-y-auto">
        {polymerTabs ? (
          /* Polymer dashboard mode: show polymer-specific tab nav */
          <>
            {isExpanded && polymerName && (
              <div className="px-6 mb-2">
                <span className="text-xs text-rs-off-white/40 uppercase tracking-wider">{polymerName}</span>
              </div>
            )}
            <ul className="flex flex-col">
              {polymerTabs.map((tab) => {
                const isActiveTab = activeTab === tab.key;
                return (
                  <li key={tab.key}>
                    <button
                      onClick={() => onTabChange?.(tab.key)}
                      className={`w-full flex items-center gap-3 py-2.5 text-sm transition-colors ${isExpanded ? 'px-6' : 'justify-center px-2'} ${
                        isActiveTab
                          ? isDark ? "bg-rs-green/10 border-l-4 border-rs-green text-rs-off-white" : "bg-rs-green/70 border-l-4 border-rs-green text-rs-green font-semibold"
                          : "text-rs-off-white/70 hover:bg-rs-jet-light hover:text-rs-off-white border-l-4 border-transparent"
                      }`}
                      title={!isExpanded ? tab.label : undefined}
                    >
                      <BarChart3
                        size={16}
                        className={`flex-shrink-0 ${isActiveTab ? 'text-rs-green' : ''}`}
                      />
                      {isExpanded && <span className="font-medium">{tab.label}</span>}
                    </button>
                  </li>
                );
              })}
            </ul>

            {/* Divider */}
            <div className="my-4 mx-4 border-t border-rs-jet-light" />
            {isExpanded && (
              <div className="px-6 mb-2">
                <span className="text-xs text-rs-off-white/40 uppercase tracking-wider">By Resin</span>
              </div>
            )}

            {/* Resin-based menus (always shown so user can switch polymers) */}
            <ul className="flex flex-col">
              {resinMenuItems.map((resin, index) => {
                const resinKey = `resin-${resin.label}`;
                const gradientClass = `dashboard-${resin.gradient}-gradient`;
                const textColorClass = `text-rs-${resin.textColor}`;
                const hasActiveChild = resin.submenuItems.some(sub => isActive(sub.path));
                const isFlyoutVisible = hoveredItem === resinKey;

                return (
                  <li key={`resin-${index}`} className="relative">
                    <div
                      onMouseEnter={(e) => handleMenuHover(resinKey, e)}
                      onMouseLeave={scheduleClose}
                      className={`flex items-center justify-between gap-3 py-2.5 cursor-default transition-colors ${isExpanded ? 'px-6' : 'justify-center'} ${
                        hasActiveChild
                          ? "bg-rs-green/10 text-rs-off-white"
                          : "text-rs-off-white/70 hover:bg-rs-jet-light hover:text-rs-off-white"
                      }`}
                    >
                      <div className={`flex items-center gap-3 ${!isExpanded ? 'justify-center w-full' : ''}`}>
                        <div className={`flex items-center justify-center w-6 h-6 rounded font-bold text-xs flex-shrink-0 ${gradientClass} ${textColorClass}`}>
                          {resin.label}
                        </div>
                        {isExpanded && <span className="text-sm font-medium">{resin.fullName}</span>}
                      </div>
                      {isExpanded && (
                        <ChevronRight size={14} className="text-rs-off-white/30 flex-shrink-0" />
                      )}
                    </div>

                    {/* Flyout with invisible bridge */}
                    {isFlyoutVisible && (
                      <div
                        className="fixed z-50"
                        style={{ top: flyoutTop - 8, left: getFlyoutLeft() - 16, paddingLeft: 16, paddingTop: 8 }}
                        onMouseEnter={() => handleFlyoutEnter(resinKey)}
                        onMouseLeave={scheduleClose}
                      >
                        <div className="bg-rs-off-black border border-rs-jet-light rounded-lg shadow-2xl min-w-48 overflow-hidden">
                          <div className="px-4 py-2.5 text-sm font-medium text-rs-off-white bg-rs-jet-light/40">
                            {resin.fullName}
                          </div>
                          <ul>
                            {resin.submenuItems
                              .filter(sub => sub.isAvailable)
                              .map((sub, subIndex) =>
                                renderSubItem(sub, String(subIndex), `poly-${resinKey}-${subIndex}`)
                              )}
                          </ul>
                        </div>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </>
        ) : (
          /* Standard mode: show category-based nav */
          <>
            <ul className="flex flex-col">
              {visibleItems.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.path);
                const isFlyoutVisible = hoveredItem === item.key && (!isExpanded || item.hasSubmenu);

                return (
                  <li key={item.key} className="relative">
                    <div
                      onClick={() => { if (!item.hasSubmenu) navigate(item.path); }}
                      onMouseEnter={(e) => handleMenuHover(item.key, e)}
                      onMouseLeave={scheduleClose}
                      className={`flex items-center justify-between gap-3 py-3 cursor-pointer transition-colors ${isExpanded ? 'px-6' : 'justify-center'} ${
                        active
                          ? "bg-rs-green/10 border-l-4 border-rs-green text-rs-off-white"
                          : "text-rs-off-white/70 hover:bg-rs-jet-light hover:text-rs-off-white"
                      }`}
                    >
                      <div className={`flex items-center gap-3 ${!isExpanded ? 'justify-center w-full' : ''}`}>
                        <Icon size={20} className={active ? "text-rs-green flex-shrink-0" : "flex-shrink-0"} />
                        {isExpanded && <span className="text-sm font-medium">{item.label}</span>}
                      </div>
                      {isExpanded && item.hasSubmenu && (
                        <ChevronRight size={14} className="text-rs-off-white/30 flex-shrink-0" />
                      )}
                    </div>

                    {isFlyoutVisible && (
                      <div
                        className="fixed z-50"
                        style={{ top: flyoutTop - 8, left: getFlyoutLeft() - 16, paddingLeft: 16, paddingTop: 8 }}
                        onMouseEnter={() => handleFlyoutEnter(item.key)}
                        onMouseLeave={scheduleClose}
                      >
                        <div className="bg-rs-off-black border border-rs-jet-light rounded-lg shadow-2xl min-w-48 overflow-hidden">
                          <div
                            className={`px-4 py-2.5 text-sm font-medium text-rs-off-white bg-rs-jet-light/40 ${!item.hasSubmenu ? 'cursor-pointer hover:bg-rs-jet-light/60' : ''}`}
                            onClick={() => { if (!item.hasSubmenu) { navigate(item.path); setHoveredItem(null); } }}
                          >
                            {item.label}
                          </div>
                          {item.hasSubmenu && item.submenuItems && (
                            <ul>
                              {(item.submenuItems as SidebarSubmenuItem[])
                                .filter(subItem => subItem.isAvailable)
                                .map((subItem, subIndex) =>
                                  renderSubItem(subItem, String(subIndex), `cat-${item.key}-${subIndex}`)
                                )}
                            </ul>
                          )}
                        </div>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>

            {/* Divider */}
            <div className="my-4 mx-4 border-t border-rs-jet-light" />
            {isExpanded && (
              <div className="px-6 mb-2">
                <span className="text-xs text-rs-off-white/40 uppercase tracking-wider">By Resin</span>
              </div>
            )}

            {/* Resin-based menus */}
            <ul className="flex flex-col">
              {resinMenuItems.map((resin, index) => {
                const resinKey = `resin-${resin.label}`;
                const gradientClass = `dashboard-${resin.gradient}-gradient`;
                const textColorClass = `text-rs-${resin.textColor}`;
                const hasActiveChild = resin.submenuItems.some(sub => isActive(sub.path));
                const isFlyoutVisible = hoveredItem === resinKey;

                return (
                  <li key={`resin-${index}`} className="relative">
                    <div
                      onMouseEnter={(e) => handleMenuHover(resinKey, e)}
                      onMouseLeave={scheduleClose}
                      className={`flex items-center justify-between gap-3 py-2.5 cursor-default transition-colors ${isExpanded ? 'px-6' : 'justify-center'} ${
                        hasActiveChild
                          ? "bg-rs-green/10 text-rs-off-white"
                          : "text-rs-off-white/70 hover:bg-rs-jet-light hover:text-rs-off-white"
                      }`}
                    >
                      <div className={`flex items-center gap-3 ${!isExpanded ? 'justify-center w-full' : ''}`}>
                        <div className={`flex items-center justify-center w-6 h-6 rounded font-bold text-xs flex-shrink-0 ${gradientClass} ${textColorClass}`}>
                          {resin.label}
                        </div>
                        {isExpanded && <span className="text-sm font-medium">{resin.fullName}</span>}
                      </div>
                      {isExpanded && (
                        <ChevronRight size={14} className="text-rs-off-white/30 flex-shrink-0" />
                      )}
                    </div>

                    {/* Flyout with invisible bridge */}
                    {isFlyoutVisible && (
                      <div
                        className="fixed z-50"
                        style={{ top: flyoutTop - 8, left: getFlyoutLeft() - 16, paddingLeft: 16, paddingTop: 8 }}
                        onMouseEnter={() => handleFlyoutEnter(resinKey)}
                        onMouseLeave={scheduleClose}
                      >
                        <div className="bg-rs-off-black border border-rs-jet-light rounded-lg shadow-2xl min-w-48 overflow-hidden">
                          <div className="px-4 py-2.5 text-sm font-medium text-rs-off-white bg-rs-jet-light/40">
                            {resin.fullName}
                          </div>
                          <ul>
                            {resin.submenuItems
                              .filter(sub => sub.isAvailable)
                              .map((sub, subIndex) =>
                                renderSubItem(sub, String(subIndex), `std-${resinKey}-${subIndex}`)
                              )}
                          </ul>
                        </div>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </nav>

    </aside>
  );
};

export default LeftSideBar;
